/**
 * <axiom-docs> — documentation behaviours for blog.docs / article.docs:
 * live search (Shopify predictive search, limited to this blog), table of
 * contents with scroll-spy, tabs, code copy buttons and the mobile drawer.
 */
(function () {
  'use strict';

  if (customElements.get('axiom-docs')) return;

  var desktop = window.matchMedia('(min-width: 990px)');

  function slugify(text, used) {
    var base = String(text)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';
    var slug = base;
    var i = 2;
    while (used.has(slug)) slug = base + '-' + i++;
    used.add(slug);
    return slug;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------- Search index helpers ---------------- */

  // Lowercases and strips accents one character at a time so the result has
  // the same length as the input and match positions map back to the source.
  function fold(text) {
    var out = '';
    for (var i = 0; i < text.length; i++) {
      var c = text[i];
      var n = c.normalize('NFD')[0] || c;
      out += n === 'đ' || n === 'Đ' ? 'd' : n.toLowerCase().length === 1 ? n.toLowerCase() : n;
    }
    return out;
  }

  function tokenize(query) {
    return fold(query)
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean)
      .slice(0, 8);
  }

  function hasAll(haystack, tokens) {
    for (var i = 0; i < tokens.length; i++) {
      if (haystack.indexOf(tokens[i]) === -1) return false;
    }
    return true;
  }

  var INDEX_TTL = 10 * 60 * 1000;
  var indexRequests = {};

  function fetchIndexPage(url, page, collected) {
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    return fetch(url + sep + 'page=' + page, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Search index ' + response.status);
        return response.json();
      })
      .then(function (data) {
        collected = collected.concat(data.articles || []);
        return data.next && page < 20 ? fetchIndexPage(url, page + 1, collected) : collected;
      });
  }

  // Splits each article into sections at its h2/h3 headings. Heading ids use
  // the same slugify() as the table of contents, so #anchors line up.
  function buildEntries(articles) {
    var parser = new DOMParser();
    return articles.map(function (article) {
      var doc = parser.parseFromString('<body>' + (article.c || '') + '</body>', 'text/html');
      var used = new Set();
      var sections = [{ h: '', p: '', id: '', x: '' }];
      var walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeType === 1) {
          if (/^H[23]$/.test(node.tagName) && node.textContent.trim()) {
            var heading = node.textContent.trim();
            var panel = node.closest('[data-tab-title]');
            sections.push({ h: heading, p: panel ? panel.getAttribute('data-tab-title') : '', id: slugify(heading, used), x: '' });
          }
          continue;
        }
        if (node.parentElement && node.parentElement.closest('h2, h3')) continue;
        sections[sections.length - 1].x += ' ' + node.nodeValue;
      }
      return {
        t: article.t,
        u: article.u,
        g: article.g,
        s: sections
          .map(function (section) {
            section.x = section.x.replace(/\s+/g, ' ').trim();
            return section;
          })
          .filter(function (section) {
            return section.h || section.x;
          })
      };
    });
  }

  function loadIndex(url) {
    if (indexRequests[url]) return indexRequests[url];
    var key = 'axiom-docs-index:v2:' + url;
    try {
      var cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.time < INDEX_TTL) {
        indexRequests[url] = Promise.resolve(prepare(cached.entries));
        return indexRequests[url];
      }
    } catch (error) {
      /* storage unavailable */
    }
    indexRequests[url] = fetchIndexPage(url, 1, []).then(function (articles) {
      var entries = buildEntries(articles);
      try {
        sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), entries: entries }));
      } catch (error) {
        /* quota or storage unavailable */
      }
      return prepare(entries);
    });
    indexRequests[url].catch(function () {
      delete indexRequests[url];
    });
    return indexRequests[url];
  }

  // Adds folded copies used for matching.
  function prepare(entries) {
    entries.forEach(function (entry) {
      entry.tf = fold(entry.t);
      entry.s.forEach(function (section) {
        section.hf = fold(((section.p || '') + ' ' + section.h).trim());
        section.xf = fold(section.x);
      });
    });
    return entries;
  }

  function count(haystack, needle) {
    var n = 0;
    var at = haystack.indexOf(needle);
    while (at !== -1 && n < 20) {
      n++;
      at = haystack.indexOf(needle, at + needle.length);
    }
    return n;
  }

  function snippetFor(section, tokens) {
    var text = section.x;
    if (!text) return '';
    var at = -1;
    tokens.forEach(function (token) {
      var found = section.xf.indexOf(token);
      if (found > -1 && (at === -1 || found < at)) at = found;
    });
    var max = 170;
    if (at < 70 || text.length <= max) return text.length > max ? text.slice(0, max).replace(/\s+\S*$/, '') + '…' : text;
    var start = text.lastIndexOf(' ', at - 50);
    start = start < 0 ? 0 : start + 1;
    var slice = text.slice(start, start + max);
    if (start + max < text.length) slice = slice.replace(/\s+\S*$/, '') + '…';
    return '…' + slice;
  }

  function searchIndex(entries, tokens) {
    if (!tokens.length) return [];
    var phrase = tokens.join(' ');
    var hits = [];
    entries.forEach(function (entry) {
      var titleHit = hasAll(entry.tf, tokens);
      var local = [];
      entry.s.forEach(function (section, index) {
        var score = 0;
        if (section.h && hasAll(section.hf, tokens)) {
          score = 60 + (section.hf.indexOf(phrase) === 0 ? 15 : 0);
        } else if (hasAll(section.xf, tokens)) {
          score = 10 + Math.min(count(section.xf, tokens[0]), 10) + (section.xf.indexOf(phrase) > -1 ? 8 : 0);
        }
        if (score) local.push({ section: section, index: index, score: score });
      });

      if (titleHit) {
        var best = local.length ? local.slice().sort(function (a, b) { return b.score - a.score; })[0].section : entry.s[0];
        hits.push({
          title: entry.t,
          group: entry.g,
          url: entry.u,
          heading: '',
          snippet: best ? snippetFor(best, tokens) : '',
          score: 100 + (entry.tf.indexOf(phrase) === 0 ? 30 : 0) + (entry.tf === phrase ? 40 : 0)
        });
      }

      // A phrase spread across several sections still finds the article.
      if (!titleHit && !local.length) {
        var all = entry.tf + ' ' + entry.s.map(function (s) { return s.hf + ' ' + s.xf; }).join(' ');
        if (hasAll(all, tokens)) {
          hits.push({ title: entry.t, group: entry.g, url: entry.u, heading: '', snippet: entry.s[0] ? snippetFor(entry.s[0], tokens) : '', score: 5 });
        }
      }

      local
        .sort(function (a, b) {
          return b.score - a.score;
        })
        .slice(0, 3)
        .forEach(function (hit) {
          var section = hit.section;
          if (!section.h && titleHit) return;
          hits.push({
            title: entry.t,
            group: entry.g,
            panel: section.p || '',
            url: entry.u + (section.id ? '#' + section.id : ''),
            heading: section.h,
            snippet: snippetFor(section, tokens),
            score: hit.score - hit.index * 0.01
          });
        });
    });
    return hits
      .sort(function (a, b) {
        return b.score - a.score;
      })
      .slice(0, 40);
  }

  // Escapes text and wraps every token match in <mark>.
  function mark(text, tokens) {
    var folded = fold(text);
    var ranges = [];
    tokens.forEach(function (token) {
      var at = folded.indexOf(token);
      while (at !== -1) {
        ranges.push([at, at + token.length]);
        at = folded.indexOf(token, at + token.length);
      }
    });
    if (!ranges.length) return escapeHtml(text);
    ranges.sort(function (a, b) {
      return a[0] - b[0];
    });
    var merged = [ranges[0]];
    ranges.slice(1).forEach(function (range) {
      var last = merged[merged.length - 1];
      if (range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
      else merged.push(range);
    });
    var out = '';
    var cursor = 0;
    merged.forEach(function (range) {
      out += escapeHtml(text.slice(cursor, range[0])) + '<mark>' + escapeHtml(text.slice(range[0], range[1])) + '</mark>';
      cursor = range[1];
    });
    return out + escapeHtml(text.slice(cursor));
  }

  class AxiomDocs extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.blogHandle = this.dataset.blogHandle;
      this.content = this.querySelector('[data-docs-content]');
      this.initTabs();
      this.initToc();
      this.revealHash();
      window.addEventListener('hashchange', () => this.revealHash(), { signal: this.controller.signal });
      this.initCode();
      this.initSearch();
      this.initDrawer();
      this.revealActiveLink();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.modal?.remove();
      this.tocObserver?.disconnect();
    }

    /* ---------------- Table of contents ---------------- */
    initToc() {
      var toc = this.querySelector('[data-docs-toc]');
      if (!toc || !this.content) return;
      this.toc = toc;
      this.tocList = toc.querySelector('[data-docs-toc-list]');
      this.allHeadings = Array.from(this.content.querySelectorAll('h2, h3')).filter(function (h) {
        return h.textContent.trim();
      });

      var used = new Set();
      this.allHeadings.forEach(function (heading) {
        if (!heading.id) heading.id = slugify(heading.textContent, used);
        // Anchor link on the heading itself.
        var anchor = document.createElement('a');
        anchor.className = 'docs-anchor';
        anchor.href = '#' + heading.id;
        anchor.setAttribute('aria-hidden', 'true');
        anchor.tabIndex = -1;
        anchor.textContent = '#';
        heading.appendChild(anchor);
      });

      this.renderToc();
      this.content.addEventListener('docs:tabchange', () => this.renderToc(), {
        signal: this.controller.signal,
      });
    }

    // Lists only headings that are currently shown, so inactive tab panels
    // don't flood the table of contents.
    renderToc() {
      this.tocObserver?.disconnect();
      var list = this.tocList;
      list.textContent = '';
      var headings = this.allHeadings.filter(function (h) {
        return !h.closest('[hidden]');
      });
      if (headings.length < 2) {
        this.toc.hidden = true;
        return;
      }

      var links = new Map();
      headings.forEach(function (heading) {
        var item = document.createElement('li');
        item.className = 'docs-toc__item docs-toc__item--' + heading.tagName.toLowerCase();
        var link = document.createElement('a');
        link.href = '#' + heading.id;
        link.textContent = heading.firstChild.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);
        links.set(heading, link);
      });
      this.toc.hidden = false;

      var visible = new Set();
      var setActive = function () {
        var active = headings.find(function (h) {
          return visible.has(h);
        });
        if (!active) return;
        links.forEach(function (link, heading) {
          link.classList.toggle('is-active', heading === active);
        });
      };
      this.tocObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visible.add(entry.target);
            else visible.delete(entry.target);
          });
          setActive();
        },
        { rootMargin: '-15% 0px -70% 0px' }
      );
      headings.forEach((h) => this.tocObserver.observe(h));
      links.get(headings[0]).classList.add('is-active');
    }

    /* ---------------- Tabs (.docs-tabs > .docs-tabs__panel[data-tab-title]) ---------------- */
    initTabs() {
      if (!this.content) return;
      this.content.querySelectorAll('.docs-tabs').forEach(function (tabs, groupIndex) {
        if (tabs.dataset.enhanced) return;
        tabs.dataset.enhanced = 'true';
        var panels = Array.from(tabs.querySelectorAll(':scope > .docs-tabs__panel'));
        if (!panels.length) return;
        var nav = document.createElement('div');
        nav.className = 'docs-tabs__nav';
        nav.setAttribute('role', 'tablist');
        var buttons = panels.map(function (panel, i) {
          var id = 'docs-tab-' + groupIndex + '-' + i;
          panel.id = id + '-panel';
          panel.setAttribute('role', 'tabpanel');
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'docs-tabs__tab';
          button.id = id;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', panel.id);
          button.textContent = panel.dataset.tabTitle || 'Tab ' + (i + 1);
          panel.setAttribute('aria-labelledby', id);
          nav.appendChild(button);
          return button;
        });
        var select = function (index) {
          buttons.forEach(function (b, i) {
            b.setAttribute('aria-selected', String(i === index));
            b.tabIndex = i === index ? 0 : -1;
            panels[i].hidden = i !== index;
          });
          tabs.dispatchEvent(new CustomEvent('docs:tabchange', { bubbles: true }));
        };
        buttons.forEach(function (button, i) {
          button.addEventListener('click', function () {
            select(i);
          });
          button.addEventListener('keydown', function (event) {
            var next = null;
            if (event.key === 'ArrowRight') next = (i + 1) % buttons.length;
            if (event.key === 'ArrowLeft') next = (i - 1 + buttons.length) % buttons.length;
            if (next === null) return;
            event.preventDefault();
            select(next);
            buttons[next].focus();
          });
        });
        tabs.insertBefore(nav, tabs.firstChild);
        tabs.classList.add('is-enhanced');
        select(0);
      });
    }

    revealHash() {
      if (!this.content || location.hash.length < 2) return;
      var target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (!target || !this.content.contains(target)) return;
      var panel = target.closest('.docs-tabs__panel[hidden]');
      if (panel) {
        var tab = document.getElementById(panel.getAttribute('aria-labelledby'));
        if (tab) tab.click();
      }
      requestAnimationFrame(function () {
        var lenis = window.AxiomMotion?.lenis?.();
        var offset = -((document.querySelector('axiom-header')?.offsetHeight || 0) + 24);
        if (lenis) lenis.scrollTo(target, { offset: offset, immediate: true });
        else window.scrollTo(0, target.getBoundingClientRect().top + window.scrollY + offset);
      });
    }

    /* ---------------- Code copy ---------------- */
    initCode() {
      if (!this.content) return;
      this.content.querySelectorAll('pre').forEach(function (pre) {
        if (pre.querySelector('.docs-copy')) return;
        var code = pre.textContent;
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'docs-copy';
        button.textContent = 'Copy';
        button.addEventListener('click', function () {
          navigator.clipboard?.writeText(code.trim()).then(function () {
            button.textContent = 'Copied';
            setTimeout(function () {
              button.textContent = 'Copy';
            }, 1600);
          });
        });
        pre.appendChild(button);
      });
    }

    /* ---------------- Search modal ---------------- */
    initSearch() {
      var modal = this.querySelector('[data-docs-modal]');
      if (!modal) return;
      // Keep the dialog out of transformed/animated ancestors.
      document.body.appendChild(modal);
      this.modal = modal;

      var signal = this.controller.signal;
      var input = modal.querySelector('[data-docs-modal-input]');
      var list = modal.querySelector('[data-docs-modal-results]');
      var state = modal.querySelector('[data-docs-modal-state]');
      var hint = state.textContent;
      var active = -1;
      var entries = null;
      var timer = null;
      var lastTrigger = null;
      var isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

      this.querySelectorAll('[data-docs-shortcut]').forEach(function (kbd) {
        kbd.textContent = isMac ? '⌘K' : 'Ctrl K';
      });

      var setState = function (text) {
        state.textContent = text || '';
        state.hidden = !text;
      };

      var setActive = function (index) {
        var items = list.children;
        if (!items.length) return;
        active = (index + items.length) % items.length;
        Array.prototype.forEach.call(items, function (item, i) {
          var on = i === active;
          item.classList.toggle('is-active', on);
          item.firstElementChild.setAttribute('aria-selected', String(on));
        });
        input.setAttribute('aria-activedescendant', items[active].firstElementChild.id);
        items[active].scrollIntoView({ block: 'nearest' });
      };

      var render = function (query) {
        list.textContent = '';
        active = -1;
        input.removeAttribute('aria-activedescendant');
        if (!query) {
          setState(hint);
          return;
        }
        if (!entries) {
          setState('Loading\u2026');
          return;
        }
        var tokens = tokenize(query);
        var results = searchIndex(entries, tokens);
        if (!results.length) {
          setState((modal.dataset.textEmpty || 'No results for') + ' “' + query + '”');
          return;
        }
        setState('');
        list.innerHTML = results
          .map(function (r, i) {
            var crumb = r.heading ? r.title + (r.panel ? ' \u203a ' + r.panel : '') : r.group;
            return (
              '<li class="docs-hit' + (r.heading ? ' docs-hit--section' : '') + '">' +
              '<a id="docs-hit-' + i + '" role="option" aria-selected="false" href="' + escapeHtml(r.url) + '">' +
              (crumb ? '<span class="docs-hit__crumb">' + escapeHtml(crumb) + '</span>' : '') +
              '<span class="docs-hit__title">' + mark(r.heading || r.title, tokens) + '</span>' +
              (r.snippet ? '<span class="docs-hit__text">' + mark(r.snippet, tokens) + '</span>' : '') +
              '</a></li>'
            );
          })
          .join('');
        setActive(0);
      };

      var ensureIndex = function () {
        if (entries) return Promise.resolve(entries);
        if (input.value.trim()) setState('Loading…');
        return loadIndex(modal.dataset.indexUrl).then(
          function (data) {
            entries = data;
            return data;
          },
          function () {
            setState(modal.dataset.textError || 'Search is unavailable.');
            return null;
          }
        );
      };

      var open = function (initial) {
        if (!modal.hidden) return;
        lastTrigger = document.activeElement;
        modal.hidden = false;
        window.Axiom?.lockScroll();
        requestAnimationFrame(function () {
          modal.classList.add('is-open');
        });
        if (typeof initial === 'string') input.value = initial;
        input.focus();
        input.select();
        ensureIndex().then(function () {
          render(input.value.trim());
        });
      };

      var close = function () {
        if (modal.hidden) return;
        modal.classList.remove('is-open');
        window.Axiom?.unlockScroll();
        var done = function () {
          modal.hidden = true;
        };
        if (window.Axiom?.prefersReducedMotion()) done();
        else setTimeout(done, 260);
        if (lastTrigger && lastTrigger.focus) lastTrigger.focus({ preventScroll: true });
      };

      this.querySelectorAll('[data-docs-search-open]').forEach(function (button) {
        button.addEventListener('click', function () {
          open();
        }, { signal });
      });

      modal.querySelectorAll('[data-docs-modal-close]').forEach(function (el) {
        el.addEventListener('click', close, { signal });
      });

      input.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          render(input.value.trim());
        }, 60);
      }, { signal });

      list.addEventListener('mousemove', function (event) {
        var item = event.target.closest('.docs-hit');
        if (!item) return;
        var index = Array.prototype.indexOf.call(list.children, item);
        if (index !== active) setActive(index);
      }, { signal });

      list.addEventListener('click', function (event) {
        if (event.target.closest('a')) close();
      }, { signal });

      modal.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive(active + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive(active - 1);
        } else if (event.key === 'Enter' && document.activeElement === input) {
          var link = list.children[active] && list.children[active].firstElementChild;
          if (!link) return;
          event.preventDefault();
          close();
          window.location.href = link.href;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else {
          window.Axiom?.trapFocus(modal, event);
        }
      }, { signal });

      document.addEventListener('keydown', function (event) {
        var el = document.activeElement;
        var typing = el && (/input|textarea|select/i.test(el.tagName) || el.isContentEditable);
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          if (modal.hidden) open();
          else close();
        } else if (event.key === '/' && !typing && modal.hidden) {
          event.preventDefault();
          open();
        }
      }, { signal });

      // Warm the index once the page is idle so the first search is instant.
      if (window.requestIdleCallback) window.requestIdleCallback(ensureIndex, { timeout: 3000 });
      else setTimeout(ensureIndex, 1500);
    }

    /* ---------------- Mobile drawer ---------------- */
    initDrawer() {
      var toggle = this.querySelector('[data-docs-drawer-toggle]');
      var sidebar = this.querySelector('[data-docs-sidebar]');
      if (!toggle || !sidebar) return;
      var signal = this.controller.signal;
      var setOpen = (open) => {
        this.classList.toggle('is-drawer-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        if (open) window.Axiom?.lockScroll();
        else window.Axiom?.unlockScroll();
      };
      toggle.addEventListener('click', () => setOpen(!this.classList.contains('is-drawer-open')), { signal });
      sidebar.addEventListener('click', (event) => {
        if (event.target === sidebar) setOpen(false);
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-drawer-open')) setOpen(false);
      }, { signal });
      desktop.addEventListener('change', () => {
        if (desktop.matches && this.classList.contains('is-drawer-open')) setOpen(false);
      }, { signal });
    }

    revealActiveLink() {
      var link = this.querySelector('.docs-nav__link.is-active');
      var scroller = this.querySelector('.docs-sidebar__inner');
      if (!link || !scroller) return;
      var top = link.offsetTop - scroller.clientHeight / 3;
      if (top > 0) scroller.scrollTop = top;
    }
  }

  customElements.define('axiom-docs', AxiomDocs);

  /* Icon library: clicking an icon copies its name for a theme setting. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try { document.execCommand('copy'); } catch (error) {}
    area.remove();
    return Promise.resolve();
  }

  document.addEventListener('click', (event) => {
    var button = event.target.closest('[data-copy-icon]');
    if (!button) return;
    var name = button.getAttribute('data-copy-icon');
    copyText(name).then(() => {
      clearTimeout(button._copiedTimer);
      button.classList.add('is-copied');
      var status = document.querySelector('[data-icon-status]');
      if (status) status.textContent = 'Copied: ' + name;
      button._copiedTimer = setTimeout(() => button.classList.remove('is-copied'), 1400);
    });
  });

  /* Icon library filter. */
  document.addEventListener('input', (event) => {
    var field = event.target.closest('[data-icon-filter]');
    if (!field) return;
    var query = field.value.trim().toLowerCase();
    document.querySelectorAll('[data-copy-icon]').forEach((button) => {
      var hay = (button.getAttribute('data-copy-icon') + ' ' + (button.getAttribute('data-icon-label') || '')).toLowerCase();
      button.hidden = query !== '' && !hay.includes(query);
    });
    document.querySelectorAll('.docs-icons__group').forEach((group) => {
      group.hidden = !group.querySelector('[data-copy-icon]:not([hidden])');
    });
  });
})();
